import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { purchaseAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Copy, Loader2, FileText, Paperclip, XCircle, MoreVertical, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { showApiError } from '@/lib/errorHandler';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const emptyItem = () => ({
  _key: Math.random(),
  indent_id: '',
  item_id: '',
  item_name: '',
  unique_id: '',
  external_id: '',
  unit_of_measure: 'Nos',
  secondary_uom: '',
  conversion_factor: null,
  selected_uom: '',
  item_description: '',
  is_service_item: false,
  item_kind: 'item', // 'item' | 'job_work' | 'service'
  quantity: '',
  rate: '',
  gst_percent: '0',
  delivery_terms: '',
  delivery_date: '',
  payment_terms: '',
  documents_required: '',
  special_terms: '',
});

export default function CreatePRPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromIndent = searchParams.get('from') === 'indent';
  const indentIdsParam = searchParams.get('indent_ids') || '';

  const [suppliers, setSuppliers] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState({ id: '', name: '' });
  const [prItems, setPrItems] = useState([emptyItem()]);
  const [commonTerms, setCommonTerms] = useState({
    delivery_terms: '', delivery_date: '', payment_terms: '',
    documents_required: '', special_terms: '',
  });
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [quoteFile, setQuoteFile] = useState(null);
  const [priceHistory, setPriceHistory] = useState({});

  useEffect(() => { init(); }, []);

  // Fetch previous purchase prices whenever the set of selected items changes
  const itemIdsKey = prItems.map(it => it.item_id).filter(Boolean).join(',');
  useEffect(() => {
    const ids = [...new Set(prItems.map(it => it.item_id).filter(Boolean))];
    const missing = ids.filter(id => !(id in priceHistory));
    if (missing.length === 0) return;
    purchaseAPI.itemsPriceHistory(missing)
      .then(res => setPriceHistory(prev => ({ ...prev, ...res.data })))
      .catch(() => {});
  }, [itemIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const init = async () => {
    setInitLoading(true);
    try {
      const [supRes, itemRes] = await Promise.all([
        purchaseAPI.getSuppliers(),
        purchaseAPI.getItems(),
      ]);
      setSuppliers(supRes.data || []);
      setAllItems(itemRes.data || []);

      // If from indent, load indent items
      if (fromIndent && indentIdsParam) {
        const ids = indentIdsParam.split(',');
        const indentRes = await purchaseAPI.listIndent({ status: 'pending_quote' });
        const allIndentItems = (indentRes.data.suppliers || []).flatMap(s => s.items);
        const selected = allIndentItems.filter(i => ids.includes(i.id));
        if (selected.length > 0) {
          setPrItems(selected.map(i => ({
            _key: Math.random(),
            indent_id: i.id,
            item_id: i.item_id,
            item_name: i.item_name,
            unique_id: i.unique_id || '',
            external_id: i.external_id || '',
            unit_of_measure: i.unit_of_measure || 'Nos',
            item_description: '',
            is_service_item: false,
            item_kind: 'item',
            quantity: String(i.required_quantity || ''),
            rate: '',
            gst_percent: '0',
            delivery_terms: '',
            delivery_date: '',
            payment_terms: '',
            documents_required: '',
            special_terms: '',
          })));
          // Set supplier
          if (selected[0].preferred_supplier_id) {
            setSelectedSupplier({
              id: selected[0].preferred_supplier_id,
              name: selected[0].preferred_supplier_name || '',
            });
          }
        }
      }
    } catch (e) { toast.error('Failed to load data'); }
    finally { setInitLoading(false); }
  };

  const updateItem = (idx, field, value) => {
    setPrItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      // Changing the item kind (Item / Job Work / Service) clears the selection
      // and keeps is_service_item in sync (job_work + service are non-stock-buy).
      if (field === 'item_kind' && value !== it.item_kind) {
        return {
          ...it, item_kind: value, is_service_item: value !== 'item',
          item_id: '', item_name: '', unique_id: '', external_id: '',
        };
      }
      return { ...it, [field]: value };
    }));
  };

  const addItem = () => setPrItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx) => setPrItems(prev => prev.filter((_, i) => i !== idx));

  const selectItemForRow = (idx, itemId) => {
    const item = allItems.find(i => i.id === itemId);
    if (item) {
      setPrItems(prev => prev.map((it, i) => {
        if (i !== idx) return it;
        return {
          ...it,
          item_id: item.id,
          item_name: item.item_name,
          unique_id: item.unique_id || '',
          external_id: item.external_id || '',
          unit_of_measure: item.unit_of_measure || 'Nos',
          secondary_uom: item.secondary_uom || '',
          conversion_factor: item.conversion_factor || null,
          selected_uom: item.unit_of_measure || 'Nos',
        };
      }));
    }
  };

  // Filter item options by the selected kind:
  //  - item     -> stockable items (everything except Service type)
  //  - job_work  -> Service-type items that ARE inventory (sent out for
  //                 maintenance/processing and received back into stock)
  //  - service  -> Service-type items that are NON-inventory (pure services
  //                we only receive, never stock)
  const getItemOptionsForRow = (kind) => {
    const toOpt = (i) => ({ value: i.id, label: `${i.unique_id} - ${i.item_name}` });
    if (kind === 'job_work') {
      return allItems.filter(i => i.item_type === 'Service' && i.is_inventory_item === true).map(toOpt);
    }
    if (kind === 'service') {
      return allItems.filter(i => i.item_type === 'Service' && i.is_inventory_item === false).map(toOpt);
    }
    return allItems.filter(i => i.item_type !== 'Service').map(toOpt);
  };

  const copyCommonToAll = () => {
    setPrItems(prev => prev.map(it => ({
      ...it,
      delivery_terms: commonTerms.delivery_terms || it.delivery_terms,
      delivery_date: commonTerms.delivery_date || it.delivery_date,
      payment_terms: commonTerms.payment_terms || it.payment_terms,
      documents_required: commonTerms.documents_required || it.documents_required,
      special_terms: commonTerms.special_terms || it.special_terms,
    })));
    toast.success('Common terms copied to all items');
  };

  const totalValue = prItems.reduce((sum, it) => {
    return sum + (parseFloat(it.rate) || 0) * (parseFloat(it.quantity) || 0);
  }, 0);

  const handleSubmit = async () => {
    if (!selectedSupplier.id) { toast.error('Select a supplier'); return; }
    const validItems = prItems.filter(it => it.item_name || it.item_description);
    if (validItems.length === 0) { toast.error('Add at least one item'); return; }

    setLoading(true);
    try {
      const payload = {
        indent_ids: validItems.filter(i => i.indent_id).map(i => i.indent_id),
        supplier_id: selectedSupplier.id,
        supplier_name: selectedSupplier.name,
        items: validItems.map(it => ({
          indent_id: it.indent_id || '',
          item_id: it.item_id || '',
          item_name: it.item_name || '',
          unique_id: it.unique_id || '',
          external_id: it.external_id || '',
          unit_of_measure: it.selected_uom || it.unit_of_measure || 'Nos',
          item_description: it.item_description || '',
          is_service_item: it.is_service_item || false,
          item_kind: it.item_kind || 'item',
          quantity: parseFloat(it.quantity) || 0,
          rate: parseFloat(it.rate) || 0,
          line_total: (parseFloat(it.rate) || 0) * (parseFloat(it.quantity) || 0),
          gst_percent: parseFloat(it.gst_percent) || 0,
          delivery_terms: it.delivery_terms,
          delivery_date: it.delivery_date,
          payment_terms: it.payment_terms,
          documents_required: it.documents_required,
          special_terms: it.special_terms,
        })),
        common_terms: commonTerms,
      };
      const res = await purchaseAPI.createPurchaseRequest(payload);
      const prId = res.data.id;

      // Upload quote file if attached
      if (quoteFile && prId) {
        try {
          const formData = new FormData();
          formData.append('file', quoteFile);
          await purchaseAPI.uploadQuoteFile(prId, formData);
          toast.success(`PR ${res.data.pr_number} created with quotation attached`);
        } catch {
          toast.success(`PR ${res.data.pr_number} created but quote upload failed`);
        }
      } else {
        toast.success(`Purchase Request ${res.data.pr_number} created`);
      }
      navigate('/purchase/requests');
    } catch (err) { showApiError(err, 'Failed to create PR'); }
    finally { setLoading(false); }
  };

  if (initLoading) return <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">Loading...</div>;

  const supplierOptions = suppliers.map(s => ({
    value: s.id,
    label: s.supplier_name,
    sub: s.contact_email || '',
    searchStr: `${s.supplier_name} ${s.contact_email || ''}`,
  }));

  // itemOptions removed - now using getItemOptionsForRow() which filters by service type

  return (
    <div className="space-y-4 max-w-5xl" data-testid="create-pr-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" /> New Purchase Request
          </h1>
          <p className="text-xs text-muted-foreground">
            {fromIndent ? 'From indent items' : 'Direct purchase request'}
          </p>
        </div>
      </div>

      {/* Supplier Selection */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Supplier</CardTitle></CardHeader>
        <CardContent>
          <SearchableSelect
            options={supplierOptions}
            value={selectedSupplier.id}
            onSelect={(v) => {
              const s = suppliers.find(s => s.id === v);
              setSelectedSupplier({ id: v, name: s?.supplier_name || '' });
            }}
            placeholder="Search supplier..."
            emptyText="No suppliers found"
            testId="pr-supplier-select"
          />
        </CardContent>
      </Card>

      {/* Common Terms */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Common Terms</CardTitle>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copyCommonToAll} data-testid="copy-to-all-btn">
              <Copy className="w-3 h-3 mr-1" /> Copy to All Items
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Delivery Terms</Label>
            <Input value={commonTerms.delivery_terms} onChange={e => setCommonTerms(p => ({ ...p, delivery_terms: e.target.value }))}
              className="h-8 text-xs" placeholder="Ex-works, FOR..." data-testid="common-delivery-terms" />
          </div>
          <div>
            <Label className="text-xs">Delivery Date</Label>
            <Input type="date" value={commonTerms.delivery_date} onChange={e => setCommonTerms(p => ({ ...p, delivery_date: e.target.value }))}
              className="h-8 text-xs" data-testid="common-delivery-date" />
          </div>
          <div>
            <Label className="text-xs">Payment Terms</Label>
            <Input value={commonTerms.payment_terms} onChange={e => setCommonTerms(p => ({ ...p, payment_terms: e.target.value }))}
              className="h-8 text-xs" placeholder="30 days, advance..." data-testid="common-payment-terms" />
          </div>
          <div>
            <Label className="text-xs">Documents Required at Delivery</Label>
            <Input value={commonTerms.documents_required} onChange={e => setCommonTerms(p => ({ ...p, documents_required: e.target.value }))}
              className="h-8 text-xs" placeholder="Test certificate, Invoice..." data-testid="common-docs" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Special Terms</Label>
            <Input value={commonTerms.special_terms} onChange={e => setCommonTerms(p => ({ ...p, special_terms: e.target.value }))}
              className="h-8 text-xs" placeholder="Special instructions..." data-testid="common-special" />
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Items ({prItems.length})</CardTitle>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addItem} data-testid="add-item-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {prItems.map((item, idx) => (
            <div key={item._key} className="border rounded p-3 space-y-2 bg-muted/20" data-testid={`pr-item-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">Item {idx + 1}</span>
                <div className="flex items-center gap-2">
                  <Select value={item.item_kind || 'item'} onValueChange={v => updateItem(idx, 'item_kind', v)} disabled={!!item.indent_id}>
                    <SelectTrigger className="h-7 text-[11px] w-32" data-testid={`item-kind-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="item" data-testid={`item-kind-item-${idx}`}>Item</SelectItem>
                      <SelectItem value="job_work" data-testid={`item-kind-jobwork-${idx}`}>Job Work</SelectItem>
                      <SelectItem value="service" data-testid={`item-kind-service-${idx}`}>Service</SelectItem>
                    </SelectContent>
                  </Select>
                  {prItems.length > 1 && (
                    <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {!item.indent_id && (
                  <div className="col-span-2">
                    <Label className="text-[10px]">Item {item.item_kind === 'job_work' && <span className="text-amber-600">(Job Work)</span>}{item.item_kind === 'service' && <span className="text-blue-600">(Service)</span>}</Label>
                    <SearchableSelect
                      options={getItemOptionsForRow(item.item_kind)}
                      value={item.item_id}
                      onSelect={v => selectItemForRow(idx, v)}
                      placeholder={item.item_kind === 'service' ? "Search service..." : item.item_kind === 'job_work' ? "Search job-work item..." : "Search item..."}
                      emptyText={item.item_kind === 'service' ? "No service items found" : item.item_kind === 'job_work' ? "No job-work items found" : "No items"}
                      testId={`item-search-${idx}`}
                    />
                  </div>
                )}
                {item.indent_id && (
                  <div className="col-span-2">
                    <Label className="text-[10px]">Item</Label>
                    <Input value={`${item.unique_id} - ${item.item_name}`} readOnly className="h-8 text-xs bg-muted" />
                  </div>
                )}
                <div>
                  <Label className="text-[10px]">Quantity</Label>
                  <Input type="number" step="0.01" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                    className="h-8 text-xs" data-testid={`qty-${idx}`} />
                </div>
                <div>
                  <Label className="text-[10px]">UOM</Label>
                  {item.secondary_uom ? (
                    <Select value={item.selected_uom || item.unit_of_measure} onValueChange={v => updateItem(idx, 'selected_uom', v)}>
                      <SelectTrigger className="h-8 text-[10px]" data-testid={`uom-select-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={item.unit_of_measure}>{item.unit_of_measure} (Primary)</SelectItem>
                        <SelectItem value={item.secondary_uom}>{item.secondary_uom} (Secondary)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={item.selected_uom || item.unit_of_measure || 'Nos'} onValueChange={v => updateItem(idx, 'selected_uom', v)}>
                      <SelectTrigger className="h-8 text-[10px]" data-testid={`uom-select-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['Nos', 'Kg', 'Grams', 'Ltrs', 'Mtrs', 'Ft', 'Pcs', 'Box', 'Set', 'Pair', 'Roll', 'Ton', 'ML', 'Sq.Ft', 'Sq.Mtr', 'Cu.Mtr', 'Inch'].map(u => (
                          <SelectItem key={u} value={u}>{u}</SelectItem>
                        ))}
                        {item.unit_of_measure && !['Nos', 'Kg', 'Grams', 'Ltrs', 'Mtrs', 'Ft', 'Pcs', 'Box', 'Set', 'Pair', 'Roll', 'Ton', 'ML', 'Sq.Ft', 'Sq.Mtr', 'Cu.Mtr', 'Inch'].includes(item.unit_of_measure) && (
                          <SelectItem value={item.unit_of_measure}>{item.unit_of_measure}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                  {item.secondary_uom && (item.selected_uom || item.unit_of_measure) !== item.unit_of_measure && item.quantity && (
                    <p className="text-[9px] text-muted-foreground mt-0.5">= {(parseFloat(item.quantity) / (item.conversion_factor || 1)).toFixed(3)} {item.unit_of_measure}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[10px]">Rate</Label>
                  <Input type="number" step="0.01" value={item.rate} onChange={e => updateItem(idx, 'rate', e.target.value)}
                    className="h-8 text-xs" data-testid={`rate-${idx}`} />
                  {(() => {
                    const ph = item.item_id ? priceHistory[item.item_id] : null;
                    const latest = ph?.latest;
                    if (!latest) return null;
                    const entered = parseFloat(item.rate);
                    const isHigher = !isNaN(entered) && entered > latest.rate;
                    return (
                      <div className="mt-0.5" data-testid={`prev-price-${idx}`}>
                        <div className="flex items-center gap-1">
                          <span className="text-[9px] text-muted-foreground">
                            Prev: ₹{latest.rate.toLocaleString('en-IN')}{latest.date ? ` (${latest.date})` : ''}
                          </span>
                          {(ph.history?.length || 0) > 1 && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button type="button" className="text-muted-foreground hover:text-foreground" data-testid={`price-history-btn-${idx}`}>
                                  <MoreVertical className="w-3 h-3" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="end" className="w-72 p-0" data-testid={`price-history-popover-${idx}`}>
                                <div className="p-2 border-b flex items-center gap-1 text-xs font-medium">
                                  <TrendingUp className="w-3 h-3 text-indigo-600" /> Purchase Price History
                                </div>
                                <div className="max-h-56 overflow-y-auto">
                                  <table className="w-full text-[11px]">
                                    <thead className="text-muted-foreground border-b">
                                      <tr>
                                        <th className="text-left font-medium py-1 px-2">Date</th>
                                        <th className="text-right font-medium py-1 px-2">Rate</th>
                                        <th className="text-left font-medium py-1 px-2">PO</th>
                                        <th className="text-left font-medium py-1 px-2">Supplier</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ph.history.map((h, hi) => (
                                        <tr key={hi} className="border-b border-slate-100">
                                          <td className="py-1 px-2 font-mono">{h.date || '-'}</td>
                                          <td className="py-1 px-2 text-right font-medium">₹{h.rate.toLocaleString('en-IN')}</td>
                                          <td className="py-1 px-2">{h.po_number || h.pr_number}</td>
                                          <td className="py-1 px-2 truncate max-w-[80px]" title={h.supplier_name}>{h.supplier_name}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                        {isHigher && (
                          <p className="text-[9px] text-amber-600 font-medium" data-testid={`price-higher-warn-${idx}`}>
                            Above last price — admin approval required
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              {item.is_service_item && (
                <div>
                  <Label className="text-[10px]">Service Description</Label>
                  <Textarea value={item.item_description} onChange={e => updateItem(idx, 'item_description', e.target.value)}
                    className="text-xs min-h-[40px]" placeholder="Describe the service..." data-testid={`svc-desc-${idx}`} />
                </div>
              )}

              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                <div>
                  <Label className="text-[10px]">GST %</Label>
                  <Input type="number" step="0.01" value={item.gst_percent} onChange={e => updateItem(idx, 'gst_percent', e.target.value)}
                    className="h-7 text-xs" data-testid={`gst-${idx}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Delivery Terms</Label>
                  <Input value={item.delivery_terms} onChange={e => updateItem(idx, 'delivery_terms', e.target.value)}
                    className="h-7 text-xs" data-testid={`del-terms-${idx}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Delivery Date</Label>
                  <Input type="date" value={item.delivery_date} onChange={e => updateItem(idx, 'delivery_date', e.target.value)}
                    className="h-7 text-xs" data-testid={`del-date-${idx}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Payment Terms</Label>
                  <Input value={item.payment_terms} onChange={e => updateItem(idx, 'payment_terms', e.target.value)}
                    className="h-7 text-xs" data-testid={`pay-terms-${idx}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Line Total</Label>
                  <div className="h-7 flex items-center text-xs font-semibold px-2 bg-muted rounded">
                    Rs. {((parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Quote Attachment */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><Paperclip className="w-4 h-4 text-blue-600" /> Attach Supplier Quotation</CardTitle></CardHeader>
        <CardContent>
          <p className="text-[10px] text-muted-foreground mb-2">Attach the supplier&apos;s quote so approver can review during approval</p>
          <Input
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            onChange={e => setQuoteFile(e.target.files?.[0] || null)}
            className="h-8 text-xs max-w-sm"
            data-testid="pr-quote-file-input"
          />
          {quoteFile && (
            <div className="flex items-center justify-between mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700 max-w-sm">
              <span className="truncate max-w-[250px]">{quoteFile.name}</span>
              <button onClick={() => { setQuoteFile(null); }} className="text-red-500 hover:text-red-700 ml-2">
                <XCircle className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary & Submit */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Estimated Total Value (before tax)</p>
              <p className="text-lg font-bold" data-testid="total-value">Rs. {totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
            <Button onClick={handleSubmit} disabled={loading} data-testid="submit-pr-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
              Submit for Approval
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
